import { useEffect, useMemo } from 'react';

export function useChartA11y({ chartContainerID, title = 'Chart', labels = [], series = [], a11ySummary }) {
  const generatedSummary = useMemo(() => {
    if (a11ySummary) {
      return a11ySummary;
    }

    if (!series.length || !labels.length) {
      return `Data for the ${title} is unavailable.`;
    }

    let summary = `${title} chart data: `;

    series.forEach(s => {
      const seriesName = s.name || 'Data';
      summary += `Series ${seriesName}: `;

      const values = s.data.map(item => (typeof item === 'object' ? item.y : item));

      const dataPoints = values.map((value, index) => {
        const label = labels[index] || `Point ${index + 1}`;
        return `${label}: ${value}`;
      });

      summary += dataPoints.join('; ') + '. ';
    });

    return summary.trim();
  }, [title, labels, series, a11ySummary]);

  useEffect(() => {
    const container = document.getElementById(chartContainerID);
    if (!container) return;

    const labelMap = {
      'apexcharts-zoom-in-icon': 'Zoom in',
      'apexcharts-zoom-out-icon': 'Zoom out',
      'apexcharts-reset-icon': 'Reset zoom',
      'apexcharts-pan-icon': 'Pan mode',
      'apexcharts-selection-icon': 'Selection mode',
      'apexcharts-menu-icon': `Options menu for ${title}`,
      'apexcharts-download-icon': 'Download options'
    };

    const menuLabelMap = {
      exportPNG: 'Download chart as PNG',
      exportSVG: 'Download chart as SVG',
      exportCSV: 'Download chart data as CSV'
    };

    const summaryID = `${chartContainerID}-a11y-summary`;
    let hiddenTextElement = document.getElementById(summaryID);

    if (!hiddenTextElement) {
      hiddenTextElement = document.createElement('div');
      hiddenTextElement.setAttribute('id', summaryID);
      hiddenTextElement.style.cssText = `
            position: absolute;
            width: 1px;
            height: 1px;
            margin: -1px;
            border: 0;
            padding: 0;
            clip: rect(0 0 0 0);
            overflow: hidden;
            white-space: nowrap;
        `;
      container.parentNode.insertBefore(hiddenTextElement, container);
    }

    hiddenTextElement.textContent = generatedSummary;
    hiddenTextElement.setAttribute('role', 'region');
    hiddenTextElement.setAttribute('aria-label', `${title} Summary`);
    container.setAttribute('aria-describedby', summaryID);

    const addToolbarLabels = () => {
      const toolbar = container.querySelector('.apexcharts-toolbar');
      if (!toolbar) return;

      toolbar.querySelectorAll('*').forEach(el => {
        Object.entries(labelMap).forEach(([key, label]) => {
          if (el.classList.contains(key)) {
            el.setAttribute('aria-label', label);
            el.setAttribute('tabindex', '0');
            el.setAttribute('title', label);
          }
        });
      });
    };

    const addMenuLabels = () => {
      const menu = container.querySelector('.apexcharts-menu');
      if (!menu) return;

      menu.querySelectorAll('.apexcharts-menu-item').forEach(item => {
        Object.entries(menuLabelMap).forEach(([key, label]) => {
          if (item.classList.contains(key)) {
            item.setAttribute('aria-label', label);
            item.setAttribute('tabindex', '0');
            item.setAttribute('title', label);
          }
        });
      });
    };

    addToolbarLabels();

    const observer = new MutationObserver(() => {
      addToolbarLabels();
      setTimeout(addMenuLabels, 100);
    });

    observer.observe(container, { childList: true, subtree: true });

    const pollMenu = setInterval(() => {
      const menu = document.querySelector('.apexcharts-menu');
      if (menu) {
        addMenuLabels();
        clearInterval(pollMenu);
      }
    }, 300);

    const pollTimeout = setTimeout(() => clearInterval(pollMenu), 5000);

    return () => {
      observer.disconnect();
      clearInterval(pollMenu);
      clearTimeout(pollTimeout);

      if (hiddenTextElement && hiddenTextElement.parentNode) {
        hiddenTextElement.parentNode.removeChild(hiddenTextElement);
      }
      container.removeAttribute('aria-describedby');
    };
  }, [chartContainerID, generatedSummary, title]);
}
